import { Link, createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';

import { AuthenticatedMobileShell } from '../components/AuthenticatedMobileShell.tsx';
import { ConfirmModal } from '../components/ConfirmModal.tsx';
import { StatusMessage } from '../components/StatusMessage.tsx';
import { WorkspaceContainer } from '../components/WorkspaceLayout.tsx';
import { useMe } from '../lib/auth.ts';
import { useCounterparty, useDeleteCounterparty } from '../lib/counterparties.ts';
import {
  CUSTOMER_DELETE_ERROR,
  CUSTOMER_NOT_FOUND_ERROR,
  type Counterparty,
} from '../../shared/counterparty.ts';
import {
  CenteredMessage,
  CounterpartiesHeader,
  LoginScreen,
  formatDate,
} from './counterparties.tsx';

export const Route = createFileRoute('/counterparties/$id')({
  validateSearch: (search: Record<string, unknown>): { saved?: boolean } =>
    search.saved === true || search.saved === 'true' ? { saved: true } : {},
  component: CounterpartyDetailRoute,
});

export function CounterpartyDetailRoute(): React.ReactElement {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const me = useMe();

  if (pathname.endsWith('/edit')) {
    return <Outlet />;
  }

  if (me.isLoading) {
    return <CenteredMessage>Loading…</CenteredMessage>;
  }

  if (me.data == null) {
    return <LoginScreen />;
  }

  return (
    <AuthenticatedMobileShell currentPath="/counterparties/detail">
      <AuthenticatedCounterpartyDetail />
    </AuthenticatedMobileShell>
  );
}

function AuthenticatedCounterpartyDetail(): React.ReactElement {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const detail = useCounterparty(id);
  const deleteCounterparty = useDeleteCounterparty();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function confirmDelete(customer: Counterparty): Promise<void> {
    setDeleteError(null);
    try {
      await deleteCounterparty.mutateAsync(customer.id);
      await navigate({ to: '/counterparties', search: { deleted: true } });
    } catch {
      setDeleteError(CUSTOMER_DELETE_ERROR);
    }
  }

  if (detail.isPending) {
    return (
      <DetailShell title="Customer">
        <p className="text-sm text-ink-muted">Loading…</p>
      </DetailShell>
    );
  }

  if (detail.isError) {
    return (
      <DetailShell title="Customer">
        <NotFoundState />
      </DetailShell>
    );
  }

  const customer = detail.data.counterparty;

  return (
    <CustomerDetailView
      customer={customer}
      saved={search.saved === true}
      deleteOpen={deleteOpen}
      deleteError={deleteError}
      deletePending={deleteCounterparty.isPending}
      onDeleteOpen={() => setDeleteOpen(true)}
      onDeleteCancel={() => setDeleteOpen(false)}
      onDeleteConfirm={() => {
        void confirmDelete(customer);
      }}
    />
  );
}

function CustomerDetailView({
  customer,
  saved,
  deleteOpen,
  deleteError,
  deletePending,
  onDeleteOpen,
  onDeleteCancel,
  onDeleteConfirm,
}: {
  customer: Counterparty;
  saved: boolean;
  deleteOpen: boolean;
  deleteError: string | null;
  deletePending: boolean;
  onDeleteOpen: () => void;
  onDeleteCancel: () => void;
  onDeleteConfirm: () => void;
}): React.ReactElement {
  return (
    <div className="min-h-screen">
      <WorkspaceContainer as="main" className="py-8">
        <div className="space-y-6">
          <CustomerDetailHeader customer={customer} onDeleteOpen={onDeleteOpen} />
          {saved ? <StatusMessage tone="success">Customer saved.</StatusMessage> : null}
          {deleteError ? <StatusMessage tone="error">{deleteError}</StatusMessage> : null}
          <CustomerDetailSections customer={customer} />
        </div>
      </WorkspaceContainer>
      <ConfirmModal
        open={deleteOpen}
        title="Delete customer"
        confirmLabel="Delete customer"
        confirming={deletePending}
        onCancel={onDeleteCancel}
        onConfirm={onDeleteConfirm}
      >
        <p>
          Delete <span className="font-medium">{customer.name}</span>? This cannot be undone.
        </p>
      </ConfirmModal>
    </div>
  );
}

function CustomerDetailHeader({
  customer,
  onDeleteOpen,
}: {
  customer: Counterparty;
  onDeleteOpen: () => void;
}): React.ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-4 border-b border-line pb-6 lg:flex-row lg:items-end lg:justify-between">
      <div className="min-w-0 space-y-3">
        <Link
          to="/counterparties"
          className="inline-flex min-h-11 w-fit items-center justify-center gap-1.5 rounded-full border border-line bg-paper px-3.5 py-2 text-sm font-medium text-ink transition hover:border-ochre/40 hover:bg-paper-deep"
        >
          <span aria-hidden="true">←</span>
          Back
        </Link>
        <div className="min-w-0">
          <h1 className="break-words font-serif text-3xl italic text-ink sm:text-4xl">
            {customer.name}
          </h1>
          <p className="mt-2 text-[11px] uppercase tracking-[0.16em] text-ink-muted">
            Created {formatDate(customer.createdAt)}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        <Link
          to="/counterparties/$id/edit"
          params={{ id: customer.id }}
          className="inline-flex min-h-11 items-center justify-center rounded-full border border-line bg-paper px-4 py-2 text-sm font-medium text-ink transition hover:border-ochre/40 hover:bg-paper-deep"
        >
          Edit
        </Link>
        <button
          type="button"
          onClick={onDeleteOpen}
          className="inline-flex min-h-11 items-center justify-center rounded-full bg-clay px-5 py-2 text-sm font-semibold text-paper-soft shadow-[0_8px_20px_-12px_rgba(181,82,58,0.85)] transition hover:bg-clay/85"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function CustomerDetailSections({ customer }: { customer: Counterparty }): React.ReactElement {
  return (
    <section aria-label="Customer detail sections" className="grid min-w-0 gap-5 lg:grid-cols-3">
      <InfoBlock title="Details">
        <FieldLine label="Name" value={customer.name} />
        <FieldLine label="Email" value={customer.email} />
      </InfoBlock>
      <InfoBlock title="Bill To">
        <MultilineValue value={customer.billTo} />
      </InfoBlock>
      <InfoBlock title="Ship To">
        <MultilineValue value={customer.shipTo} />
      </InfoBlock>
    </section>
  );
}

function DetailShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="min-h-screen">
      <WorkspaceContainer as="main" className="py-8">
        <div className="space-y-6">
          <CounterpartiesHeader title={title} backLink={{ to: '/counterparties' }} />
          {children}
        </div>
      </WorkspaceContainer>
    </div>
  );
}

export function NotFoundState(): React.ReactElement {
  return (
    <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-line bg-paper-soft/60 px-6 py-12 text-center">
      <p className="font-serif text-lg italic text-ink-soft">{CUSTOMER_NOT_FOUND_ERROR}</p>
      <Link
        to="/counterparties"
        className="inline-flex min-h-11 items-center justify-center rounded-full bg-ochre px-5 py-2 text-sm font-semibold text-paper-soft shadow-[0_8px_20px_-12px_rgba(176,119,43,0.85)] transition hover:bg-ochre-deep"
      >
        Customers
      </Link>
    </div>
  );
}

function InfoBlock({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="space-y-4 rounded-2xl border border-line bg-paper-soft p-5 shadow-[0_10px_28px_-26px_rgba(31,27,21,0.45)]">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink-muted">
        {title}
      </h3>
      {children}
    </section>
  );
}

function FieldLine({ label, value }: { label: string; value: string | null }): React.ReactElement {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-muted">
        {label}
      </dt>
      <dd className="mt-1 break-words text-sm text-ink">{value || '—'}</dd>
    </div>
  );
}

function MultilineValue({ value }: { value: string | null }): React.ReactElement {
  return (
    <p className="whitespace-pre-wrap break-words text-sm leading-6 text-ink-soft">
      {value || '—'}
    </p>
  );
}
